import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, MessageSquare, Users, Send } from "lucide-react";
import { api, formatApiError, formatBRL } from "../../lib/api";
import { buildWhatsAppLink } from "../../components/store/Header";

const MSG_STATUS = {
  nova: "bg-[var(--brand)] text-white border-[var(--brand)]",
  em_atendimento: "bg-amber-50 text-amber-700 border-amber-200",
  respondida: "bg-green-50 text-green-700 border-green-200",
};

export default function AdminMessages({ settings, onSaveSettings }) {
  const [messages, setMessages] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [sub, setSub] = useState("mensagens");
  const [templates, setTemplates] = useState([]);

  const load = () => {
    api.get("/admin/messages").then((res) => setMessages(res.data)).catch(() => {});
    api.get("/admin/customers").then((res) => setCustomers(res.data)).catch(() => {});
  };

  useEffect(() => {
    load();
    setTemplates(settings.message_templates || []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/messages/${id}`, { status });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir esta mensagem?")) return;
    try {
      await api.delete(`/admin/messages/${id}`);
      toast.success("Mensagem excluída.");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const replyWhatsApp = (m, template) => {
    const phone = m.phone.replace(/\D/g, "");
    const full = phone.length <= 11 ? `55${phone}` : phone;
    const text = template || `Olá ${m.name}! Aqui é da Dra.Cell, sobre sua mensagem: "${m.subject || m.message.slice(0, 50)}..."`;
    window.open(buildWhatsAppLink(full, text), "_blank");
    setStatus(m.id, "respondida");
  };

  const saveTemplates = async () => {
    await onSaveSettings({ ...settings, message_templates: templates.filter((t) => t.trim()) });
  };

  return (
    <div data-testid="admin-messages">
      <div className="flex gap-2 mb-6">
        <button type="button" onClick={() => setSub("mensagens")} data-testid="sub-mensagens"
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-full font-display text-xs uppercase tracking-wider font-medium transition-colors ${sub === "mensagens" ? "bg-[#1A1816] text-white" : "text-[#5A534C] hover:bg-[#F2EDE4]"}`}>
          <MessageSquare className="w-4 h-4" /> Mensagens ({messages.length})
        </button>
        <button type="button" onClick={() => setSub("clientes")} data-testid="sub-clientes"
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-full font-display text-xs uppercase tracking-wider font-medium transition-colors ${sub === "clientes" ? "bg-[#1A1816] text-white" : "text-[#5A534C] hover:bg-[#F2EDE4]"}`}>
          <Users className="w-4 h-4" /> Clientes ({customers.length})
        </button>
        <button type="button" onClick={() => setSub("templates")} data-testid="sub-templates"
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-full font-display text-xs uppercase tracking-wider font-medium transition-colors ${sub === "templates" ? "bg-[#1A1816] text-white" : "text-[#5A534C] hover:bg-[#F2EDE4]"}`}>
          <Send className="w-4 h-4" /> Respostas Prontas
        </button>
      </div>

      {sub === "mensagens" && (
        messages.length === 0 ? (
          <p className="text-sm text-[#6B645C]" data-testid="messages-empty">Nenhuma mensagem recebida ainda. O formulário "Fale com a Dra.Cell" do site chega aqui.</p>
        ) : (
          <div className="space-y-4" data-testid="messages-list">
            {messages.map((m) => (
              <div key={m.id} className="bg-white rounded-2xl border border-[#E8E2D9] p-5" data-testid={`message-${m.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                  <p className="font-display font-semibold text-sm">
                    {m.name} <span className="font-mono text-xs text-[#6B645C]">• {m.phone} • {new Date(m.created_at).toLocaleString("pt-BR")}</span>
                  </p>
                  <select value={m.status} onChange={(e) => setStatus(m.id, e.target.value)} data-testid={`message-status-${m.id}`}
                    className={`font-mono text-[11px] uppercase tracking-wider px-3 py-1.5 rounded-full border cursor-pointer ${MSG_STATUS[m.status]}`}>
                    <option value="nova">Nova</option>
                    <option value="em_atendimento">Em atendimento</option>
                    <option value="respondida">Respondida</option>
                  </select>
                </div>
                {m.subject && <p className="text-xs font-mono text-[var(--brand)] mb-1">{m.subject}</p>}
                <p className="text-sm text-[#1A1816] mb-3">{m.message}</p>
                <div className="flex flex-wrap gap-2 items-center">
                  <button type="button" onClick={() => replyWhatsApp(m)} data-testid={`message-reply-${m.id}`}
                    className="inline-flex items-center gap-1.5 bg-[#25D366] text-white px-4 py-2 rounded-full font-display text-xs font-semibold hover:opacity-90 transition-opacity">
                    Responder no WhatsApp
                  </button>
                  {templates.length > 0 && (
                    <select defaultValue="" onChange={(e) => { if (e.target.value) replyWhatsApp(m, e.target.value); e.target.value = ""; }} data-testid={`message-template-${m.id}`}
                      className="text-xs border border-[#D5CDC2] rounded-full px-3 py-2 bg-white cursor-pointer">
                      <option value="">Resposta pronta...</option>
                      {templates.map((t, i) => (
                        <option key={i} value={t}>{t.slice(0, 45)}...</option>
                      ))}
                    </select>
                  )}
                  <button type="button" onClick={() => remove(m.id)} data-testid={`message-delete-${m.id}`}
                    className="inline-flex items-center gap-1 text-xs text-[#5A534C] hover:text-red-600 transition-colors ml-auto">
                    <Trash2 className="w-3.5 h-3.5" /> Excluir
                  </button>
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {sub === "clientes" && (
        customers.length === 0 ? (
          <p className="text-sm text-[#6B645C]" data-testid="customers-empty">Nenhum cliente ainda. Quem fizer pedidos ou enviar mensagens aparece aqui automaticamente.</p>
        ) : (
          <div className="space-y-3" data-testid="customers-list">
            {customers.map((c, i) => (
              <div key={i} className="bg-white rounded-2xl border border-[#E8E2D9] p-4 flex flex-wrap items-center justify-between gap-2" data-testid={`customer-${i}`}>
                <div>
                  <p className="font-display font-semibold text-sm">{c.name}</p>
                  <p className="font-mono text-xs text-[#6B645C]">{c.email || c.phone || ""}</p>
                </div>
                {c.orders > 0 && (
                  <p className="font-mono text-xs text-[#5A534C]">
                    {c.orders} {c.orders === 1 ? "pedido" : "pedidos"} • {formatBRL(c.total_spent)}
                  </p>
                )}
              </div>
            ))}
          </div>
        )
      )}

      {sub === "templates" && (
        <div className="max-w-xl" data-testid="templates-editor">
          <p className="text-sm text-[#6B645C] mb-4">Mensagens prontas para responder clientes rapidamente pelo WhatsApp.</p>
          <div className="space-y-3">
            {templates.map((t, i) => (
              <div key={i} className="flex gap-2 items-start">
                <textarea rows={2} value={t} data-testid={`template-${i}`}
                  onChange={(e) => setTemplates((ts) => ts.map((x, idx) => (idx === i ? e.target.value : x)))}
                  className="flex-1 bg-white border border-[#E8E2D9] rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-[var(--brand)] transition-colors resize-none" />
                <button type="button" onClick={() => setTemplates((ts) => ts.filter((_, idx) => idx !== i))} data-testid={`template-delete-${i}`}
                  className="p-2 text-[#6B645C] hover:text-red-600 transition-colors" aria-label="Excluir modelo">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
          <div className="flex gap-2 mt-4">
            <button type="button" onClick={() => setTemplates((ts) => [...ts, ""])} data-testid="template-add"
              className="px-5 py-2.5 rounded-full border border-[#D5CDC2] font-display text-xs font-semibold hover:border-[#1A1816] transition-colors">
              + Novo Modelo
            </button>
            <button type="button" onClick={saveTemplates} data-testid="templates-save"
              className="px-5 py-2.5 rounded-full bg-[#1A1816] text-white font-display text-xs font-semibold hover:bg-[var(--brand)] transition-colors">
              Salvar Modelos
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
